import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Play, Pause, X } from 'lucide-react';

interface VocabWarmUpDialogProps {
  open: boolean;
  onClose: () => void;
  vocabulary: any;
}

export function VocabWarmUpDialog({ open, onClose, vocabulary }: VocabWarmUpDialogProps) {
  // 播放状态管理 - Move hooks to top
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTermIndex, setCurrentTermIndex] = useState<number>(-1);
  const [isFirstPronunciation, setIsFirstPronunciation] = useState(true);
  const [isPlayAllMode, setIsPlayAllMode] = useState(false);
  
  // 用于跟踪当前所有词汇的数组
  const [flatTerms, setFlatTerms] = useState<any[]>([]);
  
  // 初始化词汇扁平数组
  useEffect(() => {
    if (vocabulary && vocabulary.scenarios) {
      const terms = [];
      
      for (const scenario of vocabulary.scenarios) {
        // 检查 scenario 和 scenario.terms 是否存在且为数组
        if (scenario?.terms && Array.isArray(scenario.terms)) {
          for (const term of scenario.terms) {
            terms.push({
              ...term,
              id: `${terms.length}`
            });
          }
        }
      }
      
      setFlatTerms(terms);
    }
  }, [vocabulary]);
  
  // Helper function to speak with available voices
  const speakWithVoices = (englishText: string, chineseText: string, voices: SpeechSynthesisVoice[]) => {
    console.log("Available voices:", voices.map(v => `${v.name} (${v.lang})`));
    
    // Create English speech utterance
    const englishUtterance = new SpeechSynthesisUtterance(englishText);
    englishUtterance.lang = 'en-US';
    englishUtterance.rate = 0.9;
    
    // Create Chinese speech utterance
    const chineseUtterance = new SpeechSynthesisUtterance(chineseText);
    chineseUtterance.lang = 'zh-CN';
    chineseUtterance.rate = 0.9;
    
    // Try to find different voices for English and Chinese
    // For English, prefer a male voice
    const englishVoice = voices.find(voice => 
      voice.lang.includes('en') && 
      voice.name.includes('Male') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => 
      voice.lang.includes('en') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => voice.lang.includes('en'));
    
    // For Chinese, prefer a female voice for contrast
    const chineseVoice = voices.find(voice => 
      voice.lang.includes('zh') && 
      voice.name.includes('Female') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => 
      voice.lang.includes('zh') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => voice.lang.includes('zh'));
    
    if (englishVoice) {
      console.log("Using English voice:", englishVoice.name);
      englishUtterance.voice = englishVoice;
    }
    
    if (chineseVoice) {
      console.log("Using Chinese voice:", chineseVoice.name);
      chineseUtterance.voice = chineseVoice;
    }
    
    // Play Chinese after English finishes
    englishUtterance.onend = () => {
      setTimeout(() => {
        window.speechSynthesis.speak(chineseUtterance);
      }, 400); // Small pause between languages
    };
    
    // After Chinese finishes, trigger next term if in sequence mode
    chineseUtterance.onend = () => {
      // If in sequence playing mode, go to next term
      if (isPlaying && currentTermIndex < flatTerms.length - 1) {
        setTimeout(() => {
          setCurrentTermIndex(currentTermIndex + 1);
        }, 1000); // 词汇之间的停顿1秒
      } else if (isPlaying && currentTermIndex >= flatTerms.length - 1) {
        // 播放完所有词汇，停止播放模式
        setIsPlaying(false);
        setCurrentTermIndex(-1);
        setIsPlayAllMode(false);
      }
    };
    
    // Start with English
    window.speechSynthesis.speak(englishUtterance);
  };
  
  // Helper function to speak with introduction
  const speakWithIntroduction = (englishText: string, chineseText: string, voices: SpeechSynthesisVoice[]) => {
    // Create introduction utterance
    const introUtterance = new SpeechSynthesisUtterance("让我们先来复习一下用到的英语搭配吧");
    introUtterance.lang = 'zh-CN';
    introUtterance.rate = 0.9;
    
    // Find a female Chinese voice
    const chineseVoice = voices.find(voice => 
      voice.lang.includes('zh') && 
      voice.name.includes('Female') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => 
      voice.lang.includes('zh') && 
      (voice.name.includes('Google') || voice.name.includes('Microsoft'))
    ) || voices.find(voice => voice.lang.includes('zh'));
    
    if (chineseVoice) {
      console.log("Using Chinese voice for intro:", chineseVoice.name);
      introUtterance.voice = chineseVoice;
    }
    
    // After intro finishes, play the vocabulary item with delay
    introUtterance.onend = () => {
      setTimeout(() => {
        speakWithVoices(englishText, chineseText, voices);
      }, 1000); // 1 second delay
    };
    
    // Start with introduction
    window.speechSynthesis.speak(introUtterance);
  };
  
  // Function to speak text with introduction for first time
  const playTerm = (term: any) => {
    if (!term || !term.term) return;
    
    console.log(`Playing term: ${term.term}, has explanation: ${Boolean(term.explanation)}`);
    
    // 确保我们有解释文本（否则使用空字符串）
    const explanationText = term.explanation || "";
    
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      // 取消任何正在进行的语音
      window.speechSynthesis.cancel();
      
      // 获取可用的语音
      let voices = window.speechSynthesis.getVoices();
      
      // 检查是否有可用的语音，如果没有则等待它们加载
      if (voices.length === 0) {
        // 等待语音加载
        window.speechSynthesis.onvoiceschanged = () => {
          voices = window.speechSynthesis.getVoices();
          if (isFirstPronunciation && isPlayAllMode) {
            speakWithIntroduction(term.term, explanationText, voices);
            setIsFirstPronunciation(false);
          } else {
            speakWithVoices(term.term, explanationText, voices);
          }
        };
      } else {
        if (isFirstPronunciation && isPlayAllMode) {
          speakWithIntroduction(term.term, explanationText, voices);
          setIsFirstPronunciation(false);
        } else {
          speakWithVoices(term.term, explanationText, voices);
        }
      }
    } else {
      console.error("Speech synthesis not supported in this browser");
    }
  };
  
  // 监听播放状态变化，处理播放逻辑
  useEffect(() => {
    if (isPlaying && currentTermIndex >= 0 && currentTermIndex < flatTerms.length) {
      // 获取当前词汇
      const currentTerm = flatTerms[currentTermIndex];
      
      // 播放当前词汇
      playTerm(currentTerm);
    }
  }, [isPlaying, currentTermIndex, flatTerms, isFirstPronunciation, isPlayAllMode]);
  
  // 清理播放资源
  useEffect(() => {
    return () => {
      // 取消所有正在播放的音频
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  
  // 确保vocabulary存在且有数据 - Move after hooks
  if (!vocabulary || !vocabulary.scenarios || vocabulary.scenarios.length === 0) {
    return null;
  }
  
  // 开始或停止播放
  const togglePlayAll = () => {
    if (isPlaying) {
      // 停止播放
      setIsPlaying(false);
      setCurrentTermIndex(-1);
      setIsPlayAllMode(false);
      
      // 取消当前播放的音频
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    } else {
      // 开始播放
      setIsPlaying(true);
      setCurrentTermIndex(0);
      setIsFirstPronunciation(true);
      setIsPlayAllMode(true);
    }
  };

  // 停止所有音频播放
  const stopAllAudio = () => {
    // 停止播放模式
    setIsPlaying(false);
    setCurrentTermIndex(-1);
    setIsPlayAllMode(false);
    
    // 取消当前播放的音频
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };
  
  // 处理对话框关闭
  const handleDialogClose = () => {
    stopAllAudio();
    onClose();
  };

  // 获取序号显示
  const getNumberDisplay = (index: number) => {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-black text-white text-xs">
        {index + 1}
      </span>
    );
  };

  // 计算总词汇数量
  const totalTermsCount = flatTerms.length;

  return (
    <Dialog 
      open={open} 
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          stopAllAudio();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-y-auto bg-white border-gray-200 pt-0 p-0">
        <DialogHeader className="sticky top-0 bg-white z-50 pb-3 border-b border-gray-200 shadow-sm px-6 pt-6">
          {/* Custom close button positioned inside the sticky header */}
          <button
            onClick={handleDialogClose}
            className="absolute right-3 top-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground z-[60]"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl text-gray-900">词汇精选 <span className="text-sm text-gray-500 ml-2">共 {totalTermsCount} 个词汇</span></DialogTitle>
              <DialogDescription className="text-sm text-gray-600 mt-1">
                预习即将学习的词汇，提升学习效果
              </DialogDescription>
            </div>
            <Button 
              variant={isPlaying ? "default" : "outline"} 
              size="sm" 
              className={`flex items-center space-x-1 ${isPlaying ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'text-gray-700 hover:text-gray-900 border-gray-300'}`}
              onClick={togglePlayAll}
            >
              {isPlaying ? (
                <>
                  <Pause className="h-4 w-4 mr-1" />
                  <span>停止播放</span>
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" />
                  <span>播放全部</span>
                </>
              )}
            </Button>
          </div>
        </DialogHeader>
        
        <div className="px-6 py-4 space-y-6">
          {vocabulary.scenarios.map((scenario: any, scenarioIndex: any) => (
            <div key={scenarioIndex} className="space-y-3">
              <h3 className="text-[17px] font-semibold text-stone-700 mb-2 border-l-4 border-orange-400 pl-3">{scenario.name}</h3>
              
              <div className="overflow-hidden">
                <table className="w-full text-gray-900 table-fixed border-separate border-spacing-y-0">
                  <colgroup>
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '50%' }} />
                    <col style={{ width: '44%' }} />
                  </colgroup>
                  <tbody>
                    {scenario.terms?.map((term: any, termIndex: any) => {
                      // 找出该词在扁平数组中的索引
                      const flatIndex = flatTerms.findIndex(
                        t => t.term === term.term && t.explanation === term.explanation
                      );
                      const isActive = isPlaying && flatIndex === currentTermIndex;
                      
                      return (
                        <tr 
                          key={termIndex} 
                          className={`group transition-all duration-200 ${
                            isActive ? 'bg-orange-100' : ''
                          }`}
                        >
                          <td className="text-center align-middle">
                            {getNumberDisplay(termIndex)}
                          </td>
                          <td 
                            className="text-[18px] font-normal text-black cursor-pointer tracking-wide align-middle group-hover:text-orange-600" 
                            onClick={() => {
                              const termObj = flatTerms.find(t => 
                                t.term === term.term && t.explanation === term.explanation
                              );
                              if (termObj) {
                                playTerm(termObj);
                              }
                            }}
                          >
                            <span className="transform transition-all duration-200 group-hover:font-bold group-hover:text-orange-700 group-hover:scale-125 origin-left inline-block">
                              {term.term && term.term.charAt(0).toUpperCase() + term.term.slice(1)}
                            </span>
                           </td>
                          <td 
                            className="text-[16px] text-gray-700 font-light align-middle group-hover:text-gray-900 cursor-pointer relative"
                            onClick={() => {
                              const termObj = flatTerms.find(t => 
                                t.term === term.term && t.explanation === term.explanation
                              );
                              if (termObj) {
                                playTerm(termObj);
                              }
                            }}
                          >
                            <span>{term.explanation}</span>
                            <span className="hidden group-hover:inline-block ml-2 opacity-80 text-[16px] absolute right-2">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block animate-pulse text-orange-600" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                              </svg>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
} 